import React, { useState, useEffect, useCallback, useRef } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Loader2, Eraser, RefreshCw } from "lucide-react";
import { useRowSelection, FailedSitesNotice, LoadingSitesNotice, usePollWhileLoading } from "@/components/rowSelection";

const rowKey = (r) => `${r.site_id}:${r.product_id}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

export default function ExpiredSalesPanel({ siteIds }) {
  const [rows, setRows] = useState([]);
  const [failed, setFailed] = useState([]);
  const [loadingSites, setLoadingSites] = useState([]);
  const [loadError, setLoadError] = useState("");
  const sel = useRowSelection();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const reqId = useRef(0);

  const load = useCallback(async (quiet = false) => {
    const id = ++reqId.current;
    if (!quiet) setLoading(true);
    setLoadError("");
    try {
      const r = await api.get("/catalog/expired-sales", { params: { site_ids: siteIds.join(",") } });
      if (id !== reqId.current) return;
      setRows(r.data.rows);
      setFailed(r.data.failed_sites);
      setLoadingSites(r.data.loading_sites || []);
    } catch (e) {
      if (id === reqId.current) {
        setRows([]);
        setLoadError(apiError(e));
      }
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [siteIds]);

  useEffect(() => {
    setRows([]);
    sel.reset();
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const quietReload = useCallback(() => load(true), [load]);
  usePollWhileLoading(loadingSites, quietReload);

  const isOn = (r) => sel.isOn(rowKey(r));
  const chosen = rows.filter(isOn);
  const toggle = sel.toggle;

  const clear = async () => {
    setConfirm(false);
    setBusy(true);
    try {
      const items = chosen.map((r) => ({ site_id: r.site_id, product_id: r.product_id }));
      const r = await api.post("/catalog/clear-expired-sales", { items });
      toast.success(`Removed ${r.data.cleared} expired sale price${r.data.cleared === 1 ? "" : "s"}`);
      if (r.data.failed.length) toast.error(`${r.data.failed.length} could not be cleared — try again`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="expired-sales-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground">
          Old sale prices whose end date has passed. Review the list, untick anything to keep, then remove.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => load()} disabled={loading} data-testid="expired-refresh">
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
          <Button onClick={() => setConfirm(true)} disabled={busy || loading || chosen.length === 0} data-testid="expired-clear-button">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eraser className="w-4 h-4" />}
            Remove {chosen.length} expired sale{chosen.length === 1 ? "" : "s"}
          </Button>
        </div>
      </div>
      <FailedSitesNotice sites={failed} />
      <LoadingSitesNotice sites={loadingSites} />
      <div className="rounded-lg border border-border overflow-hidden bg-card">
        <div className="overflow-x-auto max-h-[60vh]">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                <TableHead className="w-10">
                  <Checkbox
                    checked={rows.length > 0 && chosen.length === rows.length}
                    onCheckedChange={() => sel.setAll(chosen.length !== rows.length)}
                    data-testid="expired-select-all"
                  />
                </TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Store</TableHead>
                <TableHead>Regular</TableHead>
                <TableHead>Old sale</TableHead>
                <TableHead>Ended</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12"><Loader2 className="w-5 h-5 animate-spin inline" /></TableCell>
                </TableRow>
              ) : loadError ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12" data-testid="expired-load-error">
                    <p className="text-destructive text-sm mb-3">Couldn't load expired sales: {loadError}</p>
                    <Button variant="outline" size="sm" onClick={() => load()} data-testid="expired-retry">Retry</Button>
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-muted-foreground" data-testid="expired-empty">
                    No expired sale prices on the selected stores
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={rowKey(r)} className="border-border" data-testid={`expired-row-${r.site_id}-${r.product_id}`}>
                    <TableCell>
                      <Checkbox checked={isOn(r)} onCheckedChange={() => toggle(rowKey(r))} data-testid={`expired-select-${r.site_id}-${r.product_id}`} />
                    </TableCell>
                    <TableCell className="font-medium">
                      {r.name}
                      <div className="text-[11px] text-muted-foreground font-mono-num">{r.sku}</div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.site_name}</TableCell>
                    <TableCell className="font-mono-num text-sm">{r.regular_price || "—"}</TableCell>
                    <TableCell className="font-mono-num text-sm line-through text-muted-foreground">{r.sale_price}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtDate(r.date_on_sale_to)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {chosen.length} expired sale price{chosen.length === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              The old sale price and its dates will be removed in WooCommerce. Regular prices are not changed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="expired-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={clear} data-testid="expired-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
